// ** React Imports
import { Fragment, useState, MouseEvent, useEffect, useRef } from 'react'

// ** MUI Imports
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Table from '@mui/material/Table'
import Divider from '@mui/material/Divider'
import TableRow from '@mui/material/TableRow'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import CardHeader from '@mui/material/CardHeader'
import Typography from '@mui/material/Typography'
import TableContainer from '@mui/material/TableContainer'
import CustomChip from 'src/materio/@core/components/mui/chip'
// ** Types
import { CadenceTemplate, CadenceTemplateVariant, loadPlaybooks, PlaybookLibrary, PlaybookStore, saveTemplate, saveTemplateVariant } from '../PlaybookStore'
import { Button, IconButton, InputAdornment, Menu, MenuItem, TextField, Tooltip } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit';
import NameDialogWithTemplate from '../NameDialogWithTemplate'
import { client } from '../../../utils/axios-client'
import { v4 as uuid } from 'uuid';
import EditCadenceTemplate from './EditCadenceTemplate'
import AddIcon from '@mui/icons-material/Add';
import EyeOutline from 'mdi-material-ui/EyeOutline'
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AttachmentList from './AttachmentList'
import FileCopyIcon from '@mui/icons-material/FileCopy';
import RenderIf, { ClaimType, ClaimValue } from '../../common/RenderIf'
import DeleteIcon from '@mui/icons-material/Delete';
import useAwaitableComponent from '../../../hooks/useAwaitableComponent'
import ConfirmationDialog from '../../common/ConfirmationDialog'
import { ApiResponseType } from '../../../types'
import ApplicationStore from '../../../stores/ApplicationStore'
import VariantListDialog from './VariantListDialog'
import SearchIcon from '@mui/icons-material/Search';

const PlaybookCadenceTemplateList = () => {
    const playbook = PlaybookStore.useState(s => s.selectedPlaybook);
    const template = PlaybookStore.useState(s => s.selectedTemplate);

    const [addTemplateOpen, setAddTemplateOpen] = useState(false);

    const [editTemplateOpen, setEditTemplateOpen] = useState(false);
    const [filter, setFilter] = useState('');

    const [showVariantsOpen, setShowVariantsOpen] = useState(false);
    const [viewMode, setViewMode] = useState(false);
    const [attachmentsOpen, setAttachmentsOpen] = useState(false);
    const [filtersOpen, setFiltersOpen] = useState(false);

    const [status, execute, resolve, reject, reset] = useAwaitableComponent();
    const showModal = status !== "idle";

    //useEffect(() => {
    //    if (playbook) {
    //        onEditTemplate(playbook.templates[0]);
    //    }
    //},[])

    const onAddTemplate = () => {
        setAddTemplateOpen(true)
    }

    const handleConfirmDelete = async (row: CadenceTemplate) => {        
        try {
            const value = await execute();
            const response = await client.delete<ApiResponseType>('/api/playbooks/templates', { params: { templateLibraryId: row.id } });

            if (response.data.succeeded) {
                await loadPlaybooks('');

                ApplicationStore.update(s => {
                    s.snackbarMessage = "Template library successfully deleted";
                    s.snackbarOpen = true;
                });
            } else {
                alert('Error while deleting template library.');
            }
        } catch (err) {
            console.log(err);
        } finally {
            reset();
        }
    };



    const onSaveTemplate = async (row: CadenceTemplate) => {
        if (playbook === null) throw 'Playbook not selected';

        const newRecord = { ...row, id: uuid(), deliverTo: 'lead', templateBody: '' };

        const response = await saveTemplate(newRecord, playbook, '');

        if (response.succeeded) {
            setAddTemplateOpen(false);
            onEditTemplate(newRecord);
        }

        return response;
    }


    const onShowVariants = (row: CadenceTemplate) => {
        PlaybookStore.update(s => {
            s.selectedTemplate = row;
        })

        setShowVariantsOpen(true);
    }


    const onView = async (event: MouseEvent, row: CadenceTemplate) => {
        event.stopPropagation();
        const response = await client.get('/api/playbooks/templates', { params: { id: row.id } });

        PlaybookStore.update(s => {
            s.selectedTemplate = response.data;
        })

        PlaybookStore.update(s => {
            s.selectedTemplate = response.data;
        })
        setViewMode(true);
        setEditTemplateOpen(true);
    }



    const onEditTemplate = async (row: CadenceTemplate) => {
        const response = await client.get('/api/playbooks/templates', { params: { id: row.id } });

        PlaybookStore.update(s => {
            s.selectedTemplate = response.data;
        })

        setEditTemplateOpen(true);
    }



    const onCloseEditTemplate = () => {
        PlaybookStore.update(s => {
            s.selectedTemplate = null;
        })

        setEditTemplateOpen(false);
        setViewMode(false);
    }



    const onOpenAttachments = (event: MouseEvent, row: CadenceTemplate) => {
        event.stopPropagation();

        PlaybookStore.update(s => {
            s.selectedTemplate = row;
        })

        setAttachmentsOpen(true);
    }


    const onCloseAttachments = () => {
        PlaybookStore.update(s => {
            s.selectedTemplate = null;
        })

        setAttachmentsOpen(false);
    }

    let templates = structuredClone(playbook?.templates ?? []);
    let filteredTemplates = filter != '' ? templates.filter(s => s.name.toLowerCase().includes(filter.toLowerCase())) : templates;

    var collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
    filteredTemplates.sort((a, b) => collator.compare(a.name, b.name))
    return (
        <Fragment>
            <Card sx={{ mb: 6 }}>
                <CardHeader title='Cadence Templates' titleTypographyProps={{ variant: 'h6' }} action={
                    <RenderIf claimType={ClaimType.Playbooks} claimValue={ClaimValue.AddPlaybookTemplate}>
                        <TextField size="small" sx={{ mr: 2 }} value={filter} onChange={(e) => setFilter(e.target.value)} InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}

                            inputProps={{
                                style: {
                                    padding: 4.5
                                }
                            }} />
                        <Button onClick={onAddTemplate} variant='contained' sx={{ mr: 1 }} style={{ color: 'white' }} size="small" endIcon={<AddIcon />}>
                            Add Template
                        </Button>
                    </RenderIf>
                } />

                <Divider sx={{ m: 0 }} />

                <TableContainer>
                    <Table size='small' sx={{ minWidth: 500 }}>
                        <TableHead
                            sx={{ backgroundColor: theme => (theme.palette.mode === 'light' ? 'grey.50' : 'background.default') }}
                        >
                            <TableRow>
                                <TableCell sx={{ height: '3.375rem' }}>Name</TableCell>
                                <TableCell sx={{ height: '3.375rem' }}>Template Type</TableCell>
                                <TableCell sx={{ height: '3.375rem', textAlign: 'center' }}></TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {filteredTemplates.map((item, index: number) => (
                                <TableRow hover key={index} sx={{ '&:last-of-type td': { border: 0 } }} >
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>{item.name}</Typography>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex' }}>
                                            <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>{item.templateType}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ width: 100, textAlign: 'center' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <RenderIf claimType={ClaimType.Playbooks} claimValue={ClaimValue.DeletePlaybookTemplate}>
                                                <IconButton onClick={() => handleConfirmDelete(item)} title="Delete Template Library">
                                                    <DeleteIcon fontSize='small' />
                                                </IconButton>
                                            </RenderIf>
                                            <RenderIf claimType={ClaimType.Playbooks} claimValue={ClaimValue.ViewPlaybookTemplate}>
                                                <Tooltip title='View'>
                                                    <Box>
                                                        <IconButton size='small' component='a' sx={{ textDecoration: 'none' }} onClick={(event: MouseEvent) => onView(event, item)}>
                                                            <EyeOutline fontSize='small' />
                                                        </IconButton>
                                                    </Box>
                                                </Tooltip>
                                            </RenderIf>
                                            <RenderIf claimType={ClaimType.Playbooks} claimValue={ClaimValue.EditPlaybookTemplate}>
                                                <Tooltip title='Edit'>
                                                    <Box>
                                                        <IconButton size='small' component='a' sx={{ textDecoration: 'none' }} onClick={(event: MouseEvent) => onEditTemplate(item)}>
                                                            <EditIcon fontSize='small' />
                                                        </IconButton>
                                                    </Box>
                                                </Tooltip>
                                            </RenderIf>

                                            <Tooltip title="Variants">
                                                <Box>
                                                    <IconButton size='small' component='a' sx={{ textDecoration: 'none' }} onClick={() => onShowVariants(item)}>
                                                        <FileCopyIcon fontSize='small' />
                                                    </IconButton>
                                                </Box>
                                            </Tooltip>
                                            <RenderIf claimType={ClaimType.Playbooks} claimValue={ClaimValue.AddTemplateAttachments}>
                                                <Tooltip title={`${item.attachments.length} Attachments`}>
                                                    <Box>
                                                        <IconButton size='small' component='a' sx={{ textDecoration: 'none' }} onClick={(event: MouseEvent) => onOpenAttachments(event, item)}>
                                                            <AttachFileIcon fontSize='small' />
                                                            ({item.attachments.length})
                                                        </IconButton>
                                                    </Box>
                                                </Tooltip>
                                            </RenderIf>
                                            {/*<RowOptions row={item} onEdit={(event) => { onEditTemplate(event, item); }} />*/}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>


            {showVariantsOpen && template && <VariantListDialog open={showVariantsOpen} onClose={() => setShowVariantsOpen(false)} />}

            {addTemplateOpen && <NameDialogWithTemplate open={addTemplateOpen} onClose={() => setAddTemplateOpen(false)} onSubmit={onSaveTemplate} title="Create Template" subTitle="Please enter a name for the template" fieldLabel="Template Name" confirmButtonLabel="Create Template" record={{ name: '' } as CadenceTemplate} />}
            {editTemplateOpen && <EditCadenceTemplate open={editTemplateOpen} onClose={onCloseEditTemplate} viewMode={viewMode} />}
            {attachmentsOpen && <AttachmentList open={attachmentsOpen} onClose={onCloseAttachments} />}

            <ConfirmationDialog open={showModal} onConfirm={resolve} onCancel={reject} title="Delete?" message="Are you sure you want to delete the record?" confirmButtonText="Delete" cancelButtonText="Cancel" />
        </Fragment>
    )
}

export default PlaybookCadenceTemplateList
